import { type JSX } from 'react';
import { Outlet } from 'react-router-dom';

import { useApplyBranding } from '@/hooks/useTenantBranding';

import { ImpersonationBanner } from './impersonation-banner';
import { Sidebar } from './sidebar';
import { Topbar } from './topbar';

/**
 * Authenticated layout: full-viewport flex row that hosts a fixed-width
 * sidebar and a column with a fixed-height topbar over a scrollable main
 * region. The route's page renders via `<Outlet />` so each protected route
 * shares this chrome without re-mounting it on navigation.
 *
 * Impersonation banner sits ABOVE the topbar so it's always visible
 * regardless of scroll position.
 *
 * useApplyBranding overlays the reseller's primary color onto the
 * --volt CSS vars at runtime. A customer inherits from their parent
 * reseller; a reseller / platform reads their own. Empty / unset
 * fields leave the default tokens in place.
 */
export function AppShell(): JSX.Element {
  useApplyBranding();
  return (
    <div className="h-screen w-full flex flex-col bg-paper text-text overflow-hidden">
      <ImpersonationBanner />
      <div className="flex-1 flex min-h-0">
        <Sidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <Topbar />
          <main className="flex-1 overflow-y-auto bg-paper">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
